import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { EventView, GUID, TaskView, TodayPriorityCompletionView, TodayPriorityType } from '@ndvr/customer-api';

import { CustomerApi } from 'API';

const PriorityQueryKeys = {
    completedPriorities: (targetGUIDs: string[]) => ['todays-priorities', 'completions', ...targetGUIDs],
} as const;

export const useCompletedPriorities = (targetGUIDs: string[]) => {
    const sortedTargetGUIDs = [...targetGUIDs].sort();

    return useQuery<TodayPriorityCompletionView[]>({
        queryKey: PriorityQueryKeys.completedPriorities(sortedTargetGUIDs),
        enabled: sortedTargetGUIDs.length > 0,
        queryFn: async () => {
            const [completions] = await CustomerApi().listCompletedPriorities({
                targetGUIDs: sortedTargetGUIDs,
            });
            return completions ?? [];
        },
    });
};

export const useCompleteEventPriority = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ eventGUID, type }: { eventGUID: GUID<EventView>; type: TodayPriorityType }) => {
            await CustomerApi().completeEventPriority(eventGUID, { type });
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['todays-priorities', 'completions'],
            });
        },
    });
};

export const useCompleteTaskPriority = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ taskGUID }: { taskGUID: GUID<TaskView> }) => {
            await CustomerApi().completeTaskPriority(taskGUID);
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['todays-priorities', 'completions'],
            });
        },
    });
};
